import { useQuery } from '@tanstack/react-query';
import { AlertCircle, ArrowLeftRight, Info, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Explain } from '@/components/Explain';
import { cn } from '@/lib/cn';
import type { Bootstrap } from '@/lib/bootstrap';
import { formatTime, type TimeZoneMode } from '@/lib/format';
import type { ExchangeRateRow } from '@/types';
import { ApiError } from '@/features/bet-inspection/lib/api';
import { fetchExchangeRates } from './lib/api';

/** A rate older than this is flagged — the table is refreshed per currency. */
const STALE_DAYS = 30;
const DAY_MS = 86_400_000;

type SortKey = 'currency' | 'rate' | 'updated';

/**
 * Exchange Rates — `game_aggr.exchange_rates` as stored.
 *
 * This page exists to answer one recurring support question: "why does this
 * portal's total not match the invoice?" So it states plainly that these rates
 * are the PORTAL's, shows each pair's own `updated_at` (they are refreshed
 * per currency, and some are years old), and flags rates that cannot convert at
 * all — in production BTC and the metals are stored as '0.00'.
 */
export function ExchangeRates({
  boot,
  tz,
  base,
}: {
  boot: Bootstrap;
  tz: TimeZoneMode;
  base: string;
}) {
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<SortKey>('currency');
  const [dir, setDir] = useState<'asc' | 'desc'>('asc');

  const rates = useQuery({
    queryKey: ['exchange-rates', boot.api],
    queryFn: ({ signal }) => fetchExchangeRates({ api: boot.api!, token: boot.token, signal }),
    enabled: Boolean(boot.api),
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const all = rates.data?.rates ?? [];
  const quotedFrom = rates.data?.base ?? 'USD';

  // factor(C -> base) = rate(base) / rate(C), because every stored rate is FROM
  // USD. Same arithmetic the report conversion uses — deliberately, so this page
  // shows the multiplier a converted figure was actually built from.
  const baseRate = useMemo(() => {
    const row = all.find((r) => (r.currency_to ?? '').toUpperCase() === base.toUpperCase());
    const n = Number(row?.rate);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [all, base]);

  const rows = useMemo(() => {
    const needle = q.trim().toUpperCase();
    const filtered = needle
      ? all.filter(
          (r) =>
            (r.currency_to ?? '').toUpperCase().includes(needle) ||
            (r.currencies ?? '').toUpperCase().includes(needle),
        )
      : all;

    const sorted = [...filtered].sort((a, b) => {
      if (sort === 'currency') {
        return (a.currency_to ?? '').localeCompare(b.currency_to ?? '');
      }
      if (sort === 'rate') {
        // Numeric, not lexicographic — '9' must not outrank '100'.
        return (Number(a.rate) || 0) - (Number(b.rate) || 0);
      }
      return Date.parse(a.updated_at ?? '') - Date.parse(b.updated_at ?? '');
    });
    return dir === 'asc' ? sorted : sorted.reverse();
  }, [all, q, sort, dir]);

  const now = Date.now();
  const stale = all.filter((r) => ageDays(r, now) > STALE_DAYS).length;
  const unusable = all.filter((r) => !usable(r)).length;

  const toggleSort = (key: SortKey) => {
    if (sort === key) setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSort(key);
      setDir(key === 'currency' ? 'asc' : 'desc');
    }
  };

  return (
    <>
      <div className="sticky top-[41px] z-30 border-b border-hairline bg-bg/95 backdrop-blur">
        <div className="flex flex-wrap items-center gap-3 px-4 py-2.5">
          <label className="relative block w-[220px]">
            <Search
              size={12}
              className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-text-faint"
            />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="filter currency"
              spellCheck={false}
              autoComplete="off"
              className={cn(
                'w-full border border-hairline bg-surface-sunken py-1.5 pl-7 pr-2',
                'font-mono text-[12px] text-text placeholder:text-text-faint',
                'hover:border-hairline-strong focus:border-accent focus:outline-none',
              )}
            />
          </label>

          {rates.isSuccess && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 font-condensed text-[11px] uppercase tracking-wider">
              <span className="text-text-faint">
                <span className="tnum font-mono text-text-dim">{all.length}</span> pairs
              </span>
              {stale > 0 && (
                <span className="text-warn">
                  <span className="tnum font-mono">{stale}</span> older than {STALE_DAYS}d
                </span>
              )}
              {unusable > 0 && (
                <span className="text-crit">
                  <span className="tnum font-mono">{unusable}</span> unusable
                </span>
              )}
            </div>
          )}

          <span className="ml-auto hidden font-condensed text-[10px] uppercase tracking-wider text-text-faint xl:block">
            quoted from {quotedFrom} · base {base}
          </span>
        </div>
      </div>

      <Notice quotedFrom={quotedFrom} />

      {rates.isLoading && <Scanning />}
      {rates.isError && <Failure error={rates.error as Error} />}

      {rates.isSuccess && (
        <main className="rise overflow-x-auto">
          <table className="w-full border-collapse text-[12px]">
            <thead>
              <tr className="border-b border-hairline bg-surface">
                <Th onClick={() => toggleSort('currency')} active={sort === 'currency'} dir={dir}>
                  currency
                </Th>
                <Th onClick={() => toggleSort('rate')} active={sort === 'rate'} dir={dir} align="right">
                  1 {quotedFrom} =
                </Th>
                <Th align="right">1 unit = {quotedFrom}</Th>
                <Th align="right">
                  <Explain term="exchange_rate_invoicing" className="no-underline">
                    <span>→ {base}</span>
                  </Explain>
                </Th>
                <Th onClick={() => toggleSort('updated')} active={sort === 'updated'} dir={dir}>
                  <Explain term="exchange_rate_updated">updated</Explain>
                </Th>
                <Th>pair</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <Row key={r.id} row={r} tz={tz} base={base} baseRate={baseRate} now={now} />
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-[12px] text-text-dim">
                    No currency matches “{q}”.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </main>
      )}
    </>
  );
}

/**
 * The whole reason this page exists — stated before the numbers, not in a
 * footnote. Support gets asked "your portal says X, the invoice says Y" and the
 * answer is nearly always this.
 */
function Notice({ quotedFrom }: { quotedFrom: string }) {
  return (
    <section className="flex gap-2.5 border-b border-hairline bg-surface-sunken px-4 py-3">
      <Info size={14} className="mt-[2px] shrink-0 text-accent" />
      <div className="text-[12.5px] leading-relaxed text-text-dim">
        <p>
          These are the rates <strong className="text-text">this portal</strong> uses to convert
          figures for display — for example a player report shown in a base currency. They are{' '}
          <strong className="text-text">not necessarily the rates used to calculate an invoice</strong>
          : billing runs its own conversion, so an invoice total can legitimately differ from what
          you see here.
        </p>
        <p className="mt-1 text-text-faint">
          Every rate is quoted from {quotedFrom} (1 {quotedFrom} = rate). Each pair is refreshed
          independently, so check the “updated” column before trusting a converted total.
        </p>
      </div>
    </section>
  );
}

function Row({
  row,
  tz,
  base,
  baseRate,
  now,
}: {
  row: ExchangeRateRow;
  tz: TimeZoneMode;
  base: string;
  baseRate: number | null;
  now: number;
}) {
  const rate = Number(row.rate);
  const ok = usable(row);
  const age = ageDays(row, now);
  const isStale = age > STALE_DAYS;
  const code = (row.currency_to ?? '').toUpperCase();
  const isBase = code === base.toUpperCase();
  const toBase = ok && baseRate ? baseRate / rate : null;
  const updated = row.updated_at ? new Date(row.updated_at) : null;

  return (
    <tr className={cn('border-b border-hairline/60 hover:bg-surface-raised', !ok && 'bg-crit-bg/40')}>
      <td className="px-4 py-1.5">
        <span className="font-mono text-[12px] font-semibold text-text">{code || '—'}</span>
        {isBase && (
          <span className="ml-2 border border-accent/40 px-1 font-condensed text-[9px] uppercase tracking-wider text-accent">
            base
          </span>
        )}
      </td>

      <td className="tnum px-4 py-1.5 text-right font-mono">
        {/* The raw stored string, not a reformat: a '0.00' or a malformed value
            is the finding, and rounding it would hide it. */}
        <span className={ok ? 'text-text' : 'text-crit'}>{row.rate ?? '—'}</span>
        {!ok && (
          <span className="ml-2 font-condensed text-[10px] uppercase tracking-wider text-crit">
            cannot convert
          </span>
        )}
      </td>

      <td className="tnum px-4 py-1.5 text-right font-mono text-text-dim">
        {ok ? sig(1 / rate) : '—'}
      </td>

      <td className="tnum px-4 py-1.5 text-right font-mono">
        {toBase === null ? (
          <span className="text-text-faint">—</span>
        ) : (
          <span className="text-text-dim">{sig(toBase)}</span>
        )}
      </td>

      <td className="px-4 py-1.5">
        <span
          className={cn('tnum font-mono text-[11.5px]', isStale ? 'text-warn' : 'text-text-dim')}
          title={row.updated_at ?? ''}
        >
          {updated ? formatTime(updated, tz, true) : '—'}
        </span>
        {updated && (
          <span className={cn('ml-2 text-[11px]', isStale ? 'text-warn' : 'text-text-faint')}>
            {age < 1 ? 'today' : `${Math.floor(age)}d ago`}
          </span>
        )}
      </td>

      <td className="px-4 py-1.5 font-mono text-[11.5px] text-text-faint">{row.currencies ?? '—'}</td>
    </tr>
  );
}

/** A rate that will not parse to a positive number converts nothing. */
function usable(row: ExchangeRateRow): boolean {
  const n = Number(row.rate);
  return Number.isFinite(n) && n > 0;
}

function ageDays(row: ExchangeRateRow, now: number): number {
  const t = Date.parse(row.updated_at ?? '');
  return Number.isFinite(t) ? (now - t) / DAY_MS : Infinity;
}

/** FX factors span 0.0000001 to 100000+, so fixed decimals lie at both ends. */
function sig(n: number): string {
  if (!Number.isFinite(n)) return '—';
  if (n === 0) return '0';
  const abs = Math.abs(n);
  if (abs >= 1000) return n.toFixed(2);
  if (abs >= 1) return n.toFixed(4);
  return n.toPrecision(4);
}

function Th({
  children,
  onClick,
  active,
  dir,
  align = 'left',
}: {
  children: React.ReactNode;
  onClick?: () => void;
  active?: boolean;
  dir?: 'asc' | 'desc';
  align?: 'left' | 'right';
}) {
  const content = (
    <span
      className={cn(
        'font-condensed text-[10px] font-semibold uppercase tracking-wider',
        active ? 'text-accent' : 'text-text-faint',
      )}
    >
      {children}
      {active && <span className="ml-1">{dir === 'asc' ? '↑' : '↓'}</span>}
    </span>
  );
  return (
    <th className={cn('px-4 py-1.5', align === 'right' ? 'text-right' : 'text-left')}>
      {onClick ? (
        <button type="button" onClick={onClick} className="hover:text-text">
          {content}
        </button>
      ) : (
        content
      )}
    </th>
  );
}

function Scanning() {
  return (
    <div className="instrument-bg flex min-h-[40vh] flex-col items-center justify-center gap-4">
      <div className="lens-scan relative h-px w-[220px] overflow-hidden bg-hairline" />
      <p className="font-condensed text-[11px] uppercase tracking-[0.2em] text-text-dim">
        <ArrowLeftRight size={12} className="mr-2 inline" />
        reading the exchange rate table
      </p>
    </div>
  );
}

function Failure({ error }: { error: Error }) {
  const status = error instanceof ApiError ? error.status : 0;
  return (
    <div className="flex min-h-[30vh] items-start justify-center px-6 pt-12">
      <div className="flex max-w-[520px] gap-3 border border-crit/40 bg-crit-bg p-4">
        <AlertCircle size={16} className="mt-[2px] shrink-0 text-crit" />
        <div>
          <div className="mb-1 font-condensed text-[11px] font-semibold uppercase tracking-wider text-crit">
            {status === 403 ? 'Not permitted' : 'Could not load rates'}
          </div>
          <p className="text-[12.5px] leading-relaxed text-text">{error.message}</p>
        </div>
      </div>
    </div>
  );
}
